import dayjs from "dayjs";
import { useEffect, useMemo, useReducer, useRef, useState } from "react";
import { useNavigate } from "@entry/navigation";
import styled from "styled-components";

import { OnboardingLayoutProps } from "@components/OnboardingLayout";
import { EntryStepLayout } from "@entry/EntryStepLayout";
import { Spacing, SpacingSizes } from "@components/Spacing";
import { AccountType, BeneficiaryType, DateFormat, Flow, GoalType, Routes } from "@ctypes";
import { entryColors, entryFont } from "@entry/tokens";
import {
  useAddGoal,
  useErrorBanner,
  useGoalFlow,
  useOnboarding,
  usePersistSidecarGoal,
  useSuggestedAccountType,
  useUser,
} from "@hooks";
import { Translations, i18n } from "@i18n";
import { AccountTypeCard } from "./AccountTypeCard";
import { accountTypeCardContent } from "./content";
import { SuggestedAccountTypeCard } from "./SuggestedAccountTypeCard";
import {
  AccountTypeSelectionAction,
  AccountTypeSelectionState,
  SelectableAccountType,
} from "./types";

const JUNIOR_ROTH_IRA_TARGET_AGE = 60;

const initialState: AccountTypeSelectionState = {
  [AccountType.JuniorRothIra]: false,
  [AccountType.CustodialRothIra]: false,
  [AccountType.FiveTwoNinePlan]: false,
  [AccountType.GeneralInvesting]: false,
  [AccountType.RothIra]: false,
  [AccountType.TraditionalIra]: false,
  [AccountType.HarvestUtmaUgma]: false,
  [AccountType.Reserve]: false,
};

const reducer = (state: AccountTypeSelectionState, action: AccountTypeSelectionAction) => {
  return {
    ...initialState,
    [action.type]: !state[action.type],
  };
};

const CompareLabel = styled.div`
  font-family: ${entryFont.body};
  font-size: 14px;
  font-weight: 500;
  color: ${entryColors.textSecondary};
  margin-bottom: 4px;
`;

const AlternativesRow = styled.div`
  display: flex;
  gap: 14px;
  overflow-x: auto;
  padding-bottom: 4px;
  margin: 0 -24px;
  padding-left: 24px;
  padding-right: 24px;

  scrollbar-width: none;
  &::-webkit-scrollbar {
    display: none;
  }
`;

export const AccountTypeRoute = () => {
  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();
  const { resetToNext } = useOnboarding();

  const {
    goalProps: {
      beneficiaryId,
      beneficiaryType: beneficiaryTypeFromGoal = BeneficiaryType.Child,
      pendingInvite,
      dateOfBirth: dob,
      flow,
      goalName,
      goalType,
      isFutureBeneficiary,
    },
    setGoalProps,
  } = useGoalFlow();

  const { beneficiaries, user } = useUser();
  const { showErrorBanner } = useErrorBanner();
  const [addGoal] = useAddGoal();
  const { persistSidecarGoal } = usePersistSidecarGoal();

  const beneficiary = beneficiaries?.find((b) => b?.id === beneficiaryId);
  const taxIdProvided = Boolean(beneficiary?.taxIdProvided);

  const dateOfBirth = beneficiary?.dateOfBirth ? dayjs(beneficiary.dateOfBirth).toDate() : dob;

  const beneficiaryType = (() => {
    if (flow === Flow.AcceptSharedGoal && pendingInvite?.beneficiaryType) {
      return pendingInvite.beneficiaryType;
    }

    return beneficiaryTypeFromGoal;
  })();

  const isSharedGoalRecipient = flow === Flow.AcceptSharedGoal;

  const {
    recommendedAccountType,
    alternativeAccountTypes,
    loading: suggestionLoading,
  } = useSuggestedAccountType({
    isSharedGoal: isSharedGoalRecipient || undefined,
  });

  const getCardContent = (type: AccountType) => {
    const content = accountTypeCardContent[type];
    if (!content) return undefined;
    if (type === AccountType.Reserve) {
      const teyRate = "up to 5";
      return {
        ...content,
        teyBanner: i18n.t(Translations.AccountTypeRouteReserveTeyBanner, { teyRate }),
      };
    }
    return content;
  };

  const suggestedConfig = getCardContent(recommendedAccountType);

  const alternativeCards = useMemo(
    () =>
      alternativeAccountTypes.filter((type): type is SelectableAccountType =>
        Boolean(accountTypeCardContent[type]),
      ),
    [alternativeAccountTypes],
  );

  const [selectionState, dispatch] = useReducer(reducer, initialState);
  const hasUserSelected = useRef(false);

  useEffect(() => {
    if (suggestionLoading || hasUserSelected.current) return;
    if (selectionState[recommendedAccountType]) return;

    dispatch({ type: recommendedAccountType });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [suggestionLoading, recommendedAccountType]);

  const selectedAccountType = Object.keys(selectionState).find(
    (key) => selectionState[key as keyof AccountTypeSelectionState],
  ) as AccountType | undefined;

  const disabled = !selectedAccountType;

  const accountType = selectedAccountType ?? AccountType.GeneralInvesting;

  const isGoalFlow = [Flow.AddGoal, Flow.AcceptSharedGoal].includes(flow as Flow);

  const goToPostCustodialRoute = async () => {
    if (flow === Flow.AddGoal || flow === Flow.Accounts) {
      navigate(Routes.Accounts);
      return;
    }

    if (flow === Flow.AcceptSharedGoal) {
      navigate(Routes.Dashboard);
      return;
    }

    await resetToNext();
  };

  const handleSubmit = async () => {
    if (accountType === AccountType.HarvestUtmaUgma && beneficiaryType === BeneficiaryType.Child) {
      setGoalProps({ accountType });
      navigate(Routes.TaxGainHarvest);
      return;
    }

    setGoalProps({ accountType });

    if (goalType === GoalType.TrumpAccountSidecar) {
      const needsBeneficiarySsn =
        !taxIdProvided &&
        !isFutureBeneficiary &&
        beneficiaryType === BeneficiaryType.Child &&
        [
          AccountType.FiveTwoNinePlan,
          AccountType.JuniorRothIra,
          AccountType.CustodialRothIra,
        ].includes(accountType);

      if (needsBeneficiarySsn) {
        navigate(Routes.BeneficiarySsn);
        return;
      }

      try {
        setLoading(true);

        await persistSidecarGoal({ accountType });

        if (isGoalFlow || flow === Flow.Accounts) {
          navigate(Routes.Accounts);
        } else {
          resetToNext();
        }
      } catch (e: any) {
        const message = e.graphQLErrors?.[0]?.message;
        showErrorBanner(message);
      } finally {
        setLoading(false);
      }

      return;
    }

    if (accountType === AccountType.CustodialRothIra && beneficiaryType === BeneficiaryType.Child) {
      if (taxIdProvided && beneficiary?.id) {
        try {
          setLoading(true);

          await addGoal({
            variables: {
              accountType,
              beneficiaryId: beneficiary.id,
              goalName,
              goalType,
            },
          });

          if (user?.maritalStatus) {
            await goToPostCustodialRoute();
          } else {
            navigate(Routes.MaritalStatus);
          }
        } catch (e: any) {
          const message = e.graphQLErrors?.[0]?.message;
          showErrorBanner(message);
        } finally {
          setLoading(false);
        }

        return;
      }

      if (isFutureBeneficiary) {
        navigate(isGoalFlow ? Routes.GoalDeposit : Routes.ChildDeposit);
        return;
      }

      navigate(Routes.BeneficiarySsn);
      return;
    }

    if (isGoalFlow) {
      if (
        [AccountType.FiveTwoNinePlan, AccountType.JuniorRothIra].includes(accountType) &&
        beneficiaryType === BeneficiaryType.Child &&
        !taxIdProvided &&
        !isFutureBeneficiary
      ) {
        navigate(Routes.BeneficiarySsn);
        return;
      }

      navigate(Routes.GoalDeposit);
      return;
    }

    if (accountType === AccountType.FiveTwoNinePlan && beneficiaryType === BeneficiaryType.Child) {
      setGoalProps({ accountType });
      navigate(Routes.AccountHorizon);
      return;
    }

    if (accountType === AccountType.JuniorRothIra && beneficiaryType === BeneficiaryType.Child) {
      const targetYear = Number(
        dayjs(dateOfBirth).add(JUNIOR_ROTH_IRA_TARGET_AGE, "years").format(DateFormat.Year),
      );

      setGoalProps({ accountType, targetYear });

      if (isFutureBeneficiary) {
        navigate(Routes.ChildDeposit);
      } else {
        navigate(Routes.BeneficiarySsn);
      }
      return;
    }

    const isAdultFlow = [BeneficiaryType.Adult, BeneficiaryType.Self].includes(beneficiaryType);

    if (isAdultFlow) {
      navigate(Routes.GoalDeposit);
    } else {
      navigate(Routes.ChildDeposit);
    }
  };

  const handleSelect = (type: SelectableAccountType) => {
    hasUserSelected.current = true;
    dispatch({ type });
  };

  const props: OnboardingLayoutProps = {
    title: i18n.t(Translations.AccountTypeRouteHeader),
    subtitle: i18n.t(Translations.AccountTypeRouteSubtitle),
    hideLogout: flow !== Flow.Onboarding,
    cta: {
      primary: {
        title: i18n.t(Translations.AccountTypeRoutePrimaryCTA),
        onClick: handleSubmit,
        disabled,
        loading,
      },
    },
  };

  if (suggestionLoading) {
    return <EntryStepLayout {...props} animationVariant="sequenced" />;
  }

  return (
    <EntryStepLayout {...props} animationVariant="sequenced">
      {suggestedConfig && (
        <SuggestedAccountTypeCard
          selected={selectionState[recommendedAccountType]}
          onSelect={() => handleSelect(recommendedAccountType)}
          title={suggestedConfig.title}
          description={suggestedConfig.description}
          strength={suggestedConfig.strength}
          limitation={suggestedConfig.limitation}
          showExclusive={recommendedAccountType === AccountType.JuniorRothIra}
          teyBanner={suggestedConfig.teyBanner}
          showBrandIcon={recommendedAccountType === AccountType.Reserve}
        />
      )}

      {alternativeCards.length > 0 && (
        <>
          <Spacing size={SpacingSizes.L} />

          <CompareLabel>{i18n.t(Translations.AccountTypeRouteCompareLabel)}</CompareLabel>
          <Spacing size={SpacingSizes.S} />

          <AlternativesRow>
            {alternativeCards.map((type) => {
              const content = accountTypeCardContent[type]!;

              return (
                <AccountTypeCard
                  key={type}
                  selected={selectionState[type]}
                  onSelect={() => handleSelect(type)}
                  title={content.title}
                  description={content.description}
                  strength={content.strength}
                  limitation={content.limitation}
                  showExclusive={type === AccountType.JuniorRothIra}
                />
              );
            })}
          </AlternativesRow>
        </>
      )}
      <Spacing size={SpacingSizes.M} />
    </EntryStepLayout>
  );
};
